import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { galleryFor, IMG_FALLBACK } from '../lib/images';

// Seven-slot gallery.
//
// Slots that 404 are dropped rather than shown as broken tiles, so the page
// works while images are still going up — a model with only the front shot
// uploaded simply shows one image and no thumbnail strip. Nothing to configure
// per model as you upload; it corrects itself.
//
// ── ZOOM (Clarity, Oct 2026) ──────────────────────────────────────────────
//
// Desktop visitors clicked the photo expecting it to grow, and nothing
// happened. Now, the way Amazon does it:
//
//   - Desktop (a mouse): hovering the photo shows a lens on it and a
//     magnified view beside it, over the buying column. Moving off it closes.
//   - Click or tap, any screen: opens the full-screen viewer with every
//     photo, arrows, thumbnails and Escape to close. On desktop a click
//     inside the viewer zooms in further, following the mouse.
//
// The source photos are 2000px and up, so the magnified view stays sharp.

const ZOOM = 2.5;

function useFinePointer() {
  const [fine, setFine] = useState(false);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 1024px)');
    const set = () => setFine(mq.matches);
    set();
    if (mq.addEventListener) mq.addEventListener('change', set);
    else if (mq.addListener) mq.addListener(set);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', set);
      else if (mq.removeListener) mq.removeListener(set);
    };
  }, []);
  return fine;
}

function Lightbox({ slides, start, onClose, title }) {
  const [i, setI] = useState(start);
  const [zoomed, setZoomed] = useState(false);
  const [origin, setOrigin] = useState('50% 50%');
  const n = slides.length;
  const go = useCallback((d) => { setZoomed(false); setI((x) => (x + d + n) % n); }, [n]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [go, onClose]);

  // Swipe on phones.
  const touch = useRef(null);
  const onTouchStart = (e) => { touch.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touch.current == null) return;
    const dx = e.changedTouches[0].clientX - touch.current;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
    touch.current = null;
  };

  const s = slides[i];
  const pos = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    return `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
  };

  return (
    <div role="dialog" aria-modal="true" aria-label={`${title}: ${s.label}`}
      className="fixed inset-0 z-[80] flex flex-col bg-white" data-testid="gallery-lightbox">
      <div className="flex h-14 shrink-0 items-center justify-between gap-3 border-b border-gray-100 px-4">
        <p className="min-w-0 truncate text-sm font-semibold text-gray-900">
          {title} <span className="font-normal text-gray-500">· {s.label} ({i + 1} of {n})</span>
        </p>
        <button type="button" onClick={onClose} aria-label="Close"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-gray-700 hover:bg-gray-100">
          <svg viewBox="0 0 20 20" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            <path d="M5 5l10 10M15 5L5 15" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden"
        onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <img
          key={s.slot}
          src={s.src}
          alt={s.alt}
          onClick={(e) => { setOrigin(pos(e)); setZoomed((z) => !z); }}
          onMouseMove={(e) => { if (zoomed) setOrigin(pos(e)); }}
          style={{ transformOrigin: origin }}
          className={`max-h-full max-w-full select-none object-contain p-4 transition-transform duration-200 ${
            zoomed ? 'scale-[2.2] cursor-zoom-out' : 'md:cursor-zoom-in'
          }`}
          draggable="false"
        />
        {n > 1 && (
          <>
            <button type="button" onClick={() => go(-1)} aria-label="Previous photo"
              className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white/90 text-gray-800 shadow hover:bg-white">
              <svg viewBox="0 0 20 20" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M12.5 4.5L7 10l5.5 5.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
            <button type="button" onClick={() => go(1)} aria-label="Next photo"
              className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white/90 text-gray-800 shadow hover:bg-white">
              <svg viewBox="0 0 20 20" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M7.5 4.5L13 10l-5.5 5.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
          </>
        )}
      </div>

      {n > 1 && (
        <div className="flex shrink-0 justify-center gap-2 overflow-x-auto border-t border-gray-100 px-4 py-3">
          {slides.map((t, k) => (
            <button key={t.slot} type="button" onClick={() => { setZoomed(false); setI(k); }}
              aria-label={t.label} aria-current={k === i}
              className={`h-14 w-14 shrink-0 overflow-hidden rounded-lg border-2 bg-gray-50 ${k === i ? 'border-blue-600' : 'border-transparent hover:border-gray-300'}`}>
              <img src={t.src} alt="" className="h-full w-full object-contain p-1" />
            </button>
          ))}
        </div>
      )}
      <p className="hidden pb-2 text-center text-xs text-gray-500 md:block">Click the photo to zoom in · arrow keys for the next photo · Esc to close</p>
    </div>
  );
}

export default function ProductGallery({ model }) {
  const slides = galleryFor(model);
  const [active, setActive] = useState(0);
  const [missing, setMissing] = useState(() => new Set());
  const [open, setOpen] = useState(false);
  const [lens, setLens] = useState(null); // { x, y, w, h } in px within the frame
  const frame = useRef(null);
  const fine = useFinePointer();

  // Reset when navigating between products.
  useEffect(() => { setActive(0); setMissing(new Set()); setOpen(false); }, [model.id]);

  const markMissing = useCallback((slot) => {
    setMissing((prev) => {
      if (prev.has(slot)) return prev;
      const next = new Set(prev);
      next.add(slot);
      return next;
    });
  }, []);

  const available = slides.filter((s) => !missing.has(s.slot));
  const activeIndex = Math.min(active, Math.max(available.length - 1, 0));
  const current = available[activeIndex] || slides[0];
  const frontMissing = missing.has('front') && available.length === 0;

  const onMove = (e) => {
    if (!fine || !frame.current) return;
    const r = frame.current.getBoundingClientRect();
    // Room to the right of the photo, so the pane never runs off the page.
    const room = document.documentElement.clientWidth - 16 - (r.right + 24);
    setLens({ x: e.clientX - r.left, y: e.clientY - r.top, w: r.width, h: r.height, room });
  };

  // Magnified view: the same picture at ZOOM times the frame size, slid so
  // the point under the mouse sits in the middle of the pane.
  let pane = null;
  let lensBox = null;
  if (lens && fine && !frontMissing) {
    // A little bigger than the photo, as wide as the room beside it allows.
    const pw = Math.max(240, Math.min(lens.w * 1.25, lens.room));
    const ph = lens.h * 1.25;
    const sw = lens.w * ZOOM;
    const sh = lens.h * ZOOM;
    const left = Math.min(0, Math.max(pw - sw, -(lens.x * ZOOM - pw / 2)));
    const top = Math.min(0, Math.max(ph - sh, -(lens.y * ZOOM - ph / 2)));
    const lw = (pw / sw) * lens.w;
    const lh = (ph / sh) * lens.h;
    lensBox = {
      left: Math.max(0, Math.min(lens.w - lw, lens.x - lw / 2)),
      top: Math.max(0, Math.min(lens.h - lh, lens.y - lh / 2)),
      width: lw, height: lh,
    };
    pane = { pw, ph, sw, sh, left, top };
  }

  return (
    <div className="relative w-full min-w-0 max-w-full">
      <div
        ref={frame}
        role="button"
        tabIndex={0}
        aria-label={`${current.label}: open full screen`}
        onClick={() => !frontMissing && setOpen(true)}
        onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !frontMissing) { e.preventDefault(); setOpen(true); } }}
        onMouseMove={onMove}
        onMouseLeave={() => setLens(null)}
        data-testid="gallery-main"
        className={`relative aspect-square w-full overflow-hidden rounded-xl bg-gray-50 outline-none focus-visible:ring-2 focus-visible:ring-blue-500 sm:aspect-[4/3] md:aspect-square ${fine ? 'cursor-crosshair' : 'cursor-zoom-in'}`}
      >
        {frontMissing ? (
          <img src={IMG_FALLBACK} alt={model.title}
            className="h-full w-full object-contain p-6 opacity-60" />
        ) : (
          <img
            key={current.slot}
            src={current.src}
            alt={current.alt}
            width="1200" height="1200"
            fetchPriority={current.slot === 'front' ? 'high' : 'auto'}
            loading={current.slot === 'front' ? 'eager' : 'lazy'}
            onError={() => markMissing(current.slot)}
            className="h-full w-full object-contain p-3"
            draggable="false"
          />
        )}
        {lensBox && (
          <div aria-hidden="true" className="pointer-events-none absolute rounded border border-blue-500/70 bg-blue-500/10"
            style={lensBox} />
        )}
        {!frontMissing && (
          <span className="pointer-events-none absolute bottom-2 right-2 flex items-center gap-1 rounded-full bg-white/90 px-2.5 py-1 text-[11px] font-medium text-gray-600 shadow-sm">
            <svg viewBox="0 0 20 20" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <circle cx="8.5" cy="8.5" r="5.5" /><path d="M13 13l4 4M8.5 6v5M6 8.5h5" strokeLinecap="round" />
            </svg>
            {fine ? 'Hover to zoom · click to enlarge' : 'Tap to enlarge'}
          </span>
        )}
      </div>

      {/* Magnified view, desktop only. Sits beside the photo, over the buying
          column, and never takes clicks. */}
      {pane && (
        <div aria-hidden="true" data-testid="gallery-zoom"
          className="pointer-events-none absolute left-[calc(100%+1.5rem)] top-0 z-30 hidden overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl lg:block"
          style={{ width: pane.pw, height: pane.ph }}>
          <img src={current.src} alt=""
            className="absolute max-w-none object-contain"
            style={{ width: pane.sw, height: pane.sh, left: pane.left, top: pane.top, padding: 12 * ZOOM }} />
        </div>
      )}

      {available.length > 1 && (
        <div className="mt-3 flex w-full max-w-full gap-2 overflow-x-auto pb-1">
          {available.map((s, i) => (
            <button
              key={s.slot}
              type="button"
              onClick={() => setActive(i)}
              onMouseEnter={() => fine && setActive(i)}
              aria-label={s.label}
              aria-current={i === activeIndex}
              className={`h-16 w-16 shrink-0 overflow-hidden rounded-lg border-2 bg-gray-50 transition ${
                i === activeIndex ? 'border-blue-600' : 'border-transparent hover:border-gray-300'
              }`}
            >
              <img src={s.src} alt="" loading="lazy" width="64" height="64"
                onError={() => markMissing(s.slot)}
                className="h-full w-full object-contain p-1" />
            </button>
          ))}
        </div>
      )}

      {/* Preload the rest so switching thumbnails is instant, and so a 404
          registers before the customer clicks that thumbnail. */}
      <div className="hidden" aria-hidden="true">
        {slides.filter((s) => s.slot !== 'front' && !missing.has(s.slot)).map((s) => (
          <img key={s.slot} src={s.src} alt="" loading="lazy"
            onError={() => markMissing(s.slot)} />
        ))}
      </div>

      {/* Portalled to <body>: the photo column is sticky, which would
          otherwise keep the viewer underneath the site header. */}
      {open && available.length > 0 && createPortal(
        <Lightbox slides={available} start={activeIndex} title={model.title} onClose={() => setOpen(false)} />,
        document.body,
      )}
    </div>
  );
}
